import { Inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { CURRENT_PIPELINE, Pipeline } from '@vendasta/sales-common';

export interface PipelineStageStats {
  revenue: number;
  weightedRevenue: number;
  revenueInPeriod: number;
  weightedRevenueInPeriod: number;
  totalOpportunities: number;
}

// get PipelineStageStat with all values set to zero
export const zeroStat = () => ({
  revenue: 0,
  weightedRevenue: 0,
  revenueInPeriod: 0,
  weightedRevenueInPeriod: 0,
  totalOpportunities: 0,
});

@Injectable({
  providedIn: 'root',
})
export class PipelineStats {
  private readonly stageToStatsMap = new Map<string, PipelineStageStats>();
  private readonly updateStats$$ = new ReplaySubject<void>(1);
  public readonly totalRevenueForAllStages$: Observable<number>;
  public readonly totalWeightedRevenueForAllStages$: Observable<number>;
  readonly totalRevenueInPeriodForAllStages$: Observable<number>;
  readonly totalWeightedRevenueInPeriodForAllStages$: Observable<number>;
  public readonly totalVisibleOpportunitiesForAllStages$: Observable<number>;

  constructor(@Inject(CURRENT_PIPELINE) readonly currentPipeline$: Observable<Pipeline>) {
    const stageIds$ = currentPipeline$.pipe(map((pipeline) => pipeline?.stages?.map((s) => s.stageId)));

    const statsForStages$ = this.updateStats$$.pipe(
      withLatestFrom(stageIds$, currentPipeline$),
      map(([, ids, pipeline]) => {
        return ids.map((id) => {
          const stat = this.stageToStatsMap?.[id] ?? zeroStat();
          const stage = pipeline.stages.find((s) => s.stageId === id);

          // calculate weighted revenue
          if (stage?.probability > 0 && stage?.probability < 1) {
            stat.weightedRevenue = stat.revenue * stage?.probability;
            stat.weightedRevenueInPeriod = stat.revenueInPeriod * stage?.probability;
          }

          return stat;
        });
      }),
      map((stats) =>
        stats.reduce((acc: PipelineStageStats, value: PipelineStageStats) => {
          acc.revenue += value.revenue;
          acc.weightedRevenue += value.weightedRevenue;
          acc.revenueInPeriod += value.revenueInPeriod;
          acc.weightedRevenueInPeriod += value.weightedRevenueInPeriod;
          acc.totalOpportunities += value.totalOpportunities;

          return acc;
        }, zeroStat()),
      ),
      shareReplay(1),
    );

    this.totalRevenueForAllStages$ = statsForStages$.pipe(map((stats) => stats?.revenue));
    this.totalWeightedRevenueForAllStages$ = statsForStages$.pipe(map((stats) => stats?.weightedRevenue));
    this.totalRevenueInPeriodForAllStages$ = statsForStages$.pipe(map((stats) => stats?.revenueInPeriod));
    this.totalWeightedRevenueInPeriodForAllStages$ = statsForStages$.pipe(
      map((stats) => stats?.weightedRevenueInPeriod),
    );
    this.totalVisibleOpportunitiesForAllStages$ = statsForStages$.pipe(map((stats) => stats?.totalOpportunities));
  }

  public setStatsForStage(stageId: string, stats: PipelineStageStats): void {
    this.stageToStatsMap[stageId] = stats;
    this.updateStats$$.next();
  }

  getStatsForStage$(stageId: string): Observable<PipelineStageStats> {
    return this.updateStats$$.pipe(map(() => this.stageToStatsMap?.[stageId]));
  }

  public transferStatsBetweenStages(from: string, to: string, revenueMoved: number): void {
    if (!this.stageToStatsMap[from] || !this.stageToStatsMap[to]) {
      return;
    }

    this.stageToStatsMap[from].revenue -= revenueMoved;
    this.stageToStatsMap[from].totalOpportunities -= 1;

    this.stageToStatsMap[to].revenue += revenueMoved;
    this.stageToStatsMap[to].totalOpportunities += 1;

    this.updateStats$$.next();
  }
}
